import { useEffect } from "react";
import { api } from "../lib/api";

/**
 * Injects into document.head / body based on site content:
 * - favicon (<link rel="icon">)
 * - Google Analytics (GA4)
 * - Google Search Console verification meta tag
 * - Google Tag Manager
 * - Custom scripts (location: head | body_start | body_end)
 *
 * All injected elements are tagged with data-injected="ct" so we can clean up
 * on updates.
 */
export default function ScriptInjector() {
  useEffect(() => {
    let cancelled = false;

    const cleanup = () => {
      document.querySelectorAll('[data-injected="ct"]').forEach((el) => el.remove());
    };

    const injectRaw = (parent, position, htmlOrEl) => {
      const tmp = document.createElement("div");
      if (typeof htmlOrEl === "string") tmp.innerHTML = htmlOrEl.trim();
      const nodes = Array.from(tmp.childNodes);
      nodes.forEach((node) => {
        if (node.nodeType === 1) {
          // Element: for <script> tags we must create a fresh element for browsers to execute
          if (node.tagName === "SCRIPT") {
            const s = document.createElement("script");
            for (const attr of node.attributes) s.setAttribute(attr.name, attr.value);
            s.text = node.textContent || "";
            s.setAttribute("data-injected", "ct");
            if (position === "prepend") parent.insertBefore(s, parent.firstChild);
            else parent.appendChild(s);
          } else {
            node.setAttribute("data-injected", "ct");
            if (position === "prepend") parent.insertBefore(node, parent.firstChild);
            else parent.appendChild(node);
          }
        }
      });
    };

    (async () => {
      try {
        const { data: content } = await api.get("/content");
        if (cancelled) return;
        cleanup();

        // 1. Favicon
        if (content.favicon_url) {
          const link = document.createElement("link");
          link.rel = "icon";
          link.href = content.favicon_url;
          link.setAttribute("data-injected", "ct");
          // Remove any existing icon links (that we didn't inject) so ours wins
          document
            .querySelectorAll('link[rel="icon"]:not([data-injected="ct"])')
            .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
          document.head.appendChild(link);
        }

        // 2. Google Search Console verification
        const gsc = content?.analytics?.google_search_console_verification;
        if (gsc) {
          const meta = document.createElement("meta");
          meta.name = "google-site-verification";
          meta.content = gsc;
          meta.setAttribute("data-injected", "ct");
          document.head.appendChild(meta);
        }

        // 3. Google Analytics (GA4)
        const gaId = content?.analytics?.google_analytics_id;
        if (gaId && /^G-[A-Z0-9]+$/i.test(gaId)) {
          injectRaw(
            document.head,
            "append",
            `<script async src="https://www.googletagmanager.com/gtag/js?id=${gaId}"></script>`
          );
          injectRaw(
            document.head,
            "append",
            `<script>window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config','${gaId}');</script>`
          );
        }

        // 4. Google Tag Manager (optional)
        const gtmId = content?.analytics?.google_tag_manager_id;
        if (gtmId && /^GTM-[A-Z0-9]+$/i.test(gtmId)) {
          injectRaw(
            document.head,
            "append",
            `<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');</script>`
          );
        }

        // 5. Custom scripts
        for (const s of content?.custom_scripts || []) {
          if (!s.enabled || !s.code) continue;
          if (s.location === "head") {
            injectRaw(document.head, "append", s.code);
          } else if (s.location === "body_start") {
            injectRaw(document.body, "prepend", s.code);
          } else {
            // body_end (default)
            injectRaw(document.body, "append", s.code);
          }
        }
      } catch (e) {
        // silent
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}
